import React, { useState, useEffect, useCallback } from 'react';
import { motion, useAnimationControls, useReducedMotion } from 'framer-motion';
import Crest, { CrestShape } from './common/Crest';

interface ClubTheme {
  name: string;
  bgPattern: string;
  textColor: string;
  fontWeight: number;
  fontSize: string;
  crest: CrestShape;
  crestColor: string;
}

const clubs: ClubTheme[] = [
  {
    name: 'STOCKHOLM FC',
    bgPattern: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
    textColor: '#e2e8f0',
    fontWeight: 800,
    fontSize: '1.5rem',
    crest: 'shield',
    crestColor: 'rgba(255,255,255,0.12)',
  },
  {
    name: 'LONDON UNITED',
    bgPattern: 'linear-gradient(135deg, #7f1d1d 0%, #991b1b 50%, #b91c1c 100%)',
    textColor: '#fef2f2',
    fontWeight: 700,
    fontSize: '1.4rem',
    crest: 'pointed',
    crestColor: 'rgba(255,255,255,0.10)',
  },
  {
    name: 'BERLIN ATHLETIC',
    bgPattern: 'linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)',
    textColor: '#dbeafe',
    fontWeight: 800,
    fontSize: '1.3rem',
    crest: 'rounded',
    crestColor: 'rgba(255,255,255,0.10)',
  },
  {
    name: 'PARIS ROYAL',
    bgPattern: 'linear-gradient(135deg, #312e81 0%, #4338ca 50%, #6366f1 100%)',
    textColor: '#e0e7ff',
    fontWeight: 600,
    fontSize: '1.5rem',
    crest: 'banner',
    crestColor: 'rgba(255,255,255,0.10)',
  },
  {
    name: 'MADRID SPORTING',
    bgPattern: 'linear-gradient(135deg, #854d0e 0%, #a16207 40%, #ca8a04 100%)',
    textColor: '#fefce8',
    fontWeight: 800,
    fontSize: '1.25rem',
    crest: 'shield',
    crestColor: 'rgba(255,255,255,0.10)',
  },
  {
    name: 'MILANO CALCIO',
    bgPattern: 'linear-gradient(135deg, #064e3b 0%, #047857 50%, #059669 100%)',
    textColor: '#d1fae5',
    fontWeight: 700,
    fontSize: '1.35rem',
    crest: 'pointed',
    crestColor: 'rgba(255,255,255,0.10)',
  },
];

const AnimatedCard: React.FC = () => {
  const prefersReducedMotion = useReducedMotion();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [displayIndex, setDisplayIndex] = useState(0);
  // With reduced motion the card renders as a single static example rather
  // than a perpetual flip-and-type loop, so seed the text as already typed.
  const [typedText, setTypedText] = useState(prefersReducedMotion ? clubs[0].name : '');
  const [isTyping, setIsTyping] = useState(!prefersReducedMotion);
  const [flipCount, setFlipCount] = useState(0);
  const [displayFlipCount, setDisplayFlipCount] = useState(0);
  const controls = useAnimationControls();

  const club = clubs[displayIndex];

  const flipToNext = useCallback(async () => {
    const nextIndex = (currentIndex + 1) % clubs.length;
    const nextFlip = flipCount + 1;
    setCurrentIndex(nextIndex);
    setFlipCount(nextFlip);

    await controls.start({
      rotateY: (nextFlip * 180) - 90,
      transition: { duration: 0.3, ease: 'easeIn' },
    });

    setDisplayIndex(nextIndex);
    setDisplayFlipCount(nextFlip);
    setTypedText('');
    setIsTyping(true);

    await controls.start({
      rotateY: nextFlip * 180,
      transition: { duration: 0.3, ease: 'easeOut' },
    });
  }, [currentIndex, flipCount, controls]);

  useEffect(() => {
    if (prefersReducedMotion || !isTyping) return;
    let charIndex = 0;
    const clubName = clubs[displayIndex].name;

    const typeInterval = setInterval(() => {
      if (charIndex < clubName.length) {
        setTypedText(clubName.slice(0, charIndex + 1));
        charIndex++;
      } else {
        clearInterval(typeInterval);
        setIsTyping(false);
      }
    }, 80);

    return () => clearInterval(typeInterval);
  }, [displayIndex, isTyping, prefersReducedMotion]);

  useEffect(() => {
    if (prefersReducedMotion || isTyping) return;
    const timeout = setTimeout(() => {
      flipToNext();
    }, 2000);
    return () => clearTimeout(timeout);
  }, [isTyping, flipToNext, prefersReducedMotion]);

  return (
    <div
      className="w-[min(340px,calc(100vw-3rem))] md:w-[400px] aspect-[1.586/1]"
      style={{ perspective: '1200px' }}
    >
      <motion.div
        animate={controls}
        className="w-full h-full rounded-2xl overflow-hidden shadow-2xl"
        style={{
          background: club.bgPattern,
          transformStyle: 'preserve-3d',
        }}
      >
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage: `repeating-linear-gradient(
              45deg,
              transparent,
              transparent 10px,
              rgba(255,255,255,0.05) 10px,
              rgba(255,255,255,0.05) 20px
            )`,
          }}
        />

        <div className="absolute inset-0 flex items-center justify-center pointer-events-none"
          style={{ transform: displayFlipCount % 2 === 1 ? 'scaleX(-1)' : 'none' }}
        >
          <Crest shape={club.crest} color={club.crestColor} className="w-32 h-32" />
        </div>

        <div
          className="relative h-full p-6 flex flex-col justify-between"
          style={{ transform: displayFlipCount % 2 === 1 ? 'scaleX(-1)' : 'none' }}
        >
          <div>
            <span
              style={{
                color: club.textColor,
                fontFamily: "'Libre Franklin', sans-serif",
                fontWeight: club.fontWeight,
                fontSize: club.fontSize,
                letterSpacing: '0.02em',
              }}
            >
              {typedText}
              {isTyping && (
                <span
                  className="inline-block w-[2px] h-[1em] ml-0.5 align-middle animate-pulse"
                  style={{ backgroundColor: club.textColor }}
                />
              )}
            </span>
            <p className="mt-1 text-xs opacity-50" style={{ color: club.textColor }}>
              PAY
            </p>
          </div>

          <div className="flex items-end justify-between">
            <div className="flex gap-2 text-xs opacity-40" style={{ color: club.textColor }}>
              <span>****</span>
              <span>****</span>
              <span>****</span>
              <span>1234</span>
            </div>
            <picture>
              <source srcSet="/mastercard-logo.webp" type="image/webp" />
              <img
                src="/mastercard-logo.png"
                alt="Mastercard"
                width={160}
                height={128}
                decoding="async"
                className="h-10 md:h-12 w-auto object-contain"
              />
            </picture>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default AnimatedCard;
